import { yupResolver } from '@hookform/resolvers/yup'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { PhoneInput } from 'shared/components/MaskedInput/PhoneInput'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { convertMobileForAPI } from 'shared/helpers/formatters'
import { Handler } from 'shared/helpers/typeHelper'
import { useScrollToErrors } from 'shared/hooks/useScrollToErrors'
import {
  EmergencyContact as EmergencyContactModel,
  updateEmergencyContactInfo,
  User,
} from 'shared/models/user'
import { sizes } from 'shared/theme'
import { styled } from 'styled-components'
import { object, string } from 'yup'

import { useUserProvider } from '../../Context/UserContext'

interface EmergencyContactProps {
  nextStep: Handler
  user: User
}

const EmergencySchema = object().shape({
  name: string().required('This field is required.'),
  relationship: string().required('This field is required.'),
  email: string().email(),
  mobile: string(),
})

type EmergencyForm = {
  name: string
  relationship: string
  email: string
  mobile: string
}

export const EmergencyContact = ({ nextStep, user }: EmergencyContactProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const existingValues = user?.driver?.emergencyContact! || {}
  const { showBanner } = useUserProvider()

  const {
    control,
    handleSubmit,
    setError,

    formState: { errors },
  } = useForm<EmergencyForm>({
    mode: 'onSubmit',
    defaultValues: {},
    resolver: yupResolver(EmergencySchema),
  })

  useScrollToErrors(errors)

  const onSubmit = async (formData: EmergencyForm) => {
    if (!Boolean(formData.email) && !Boolean(formData.mobile)) {
      setError('mobile', {
        message: `Enter one of E-mail or Mobile Phone Number at minimum.`,
        type: 'notMatch',
      })
      setError('email', { message: ` `, type: 'notMatch' })
      return
    }

    setIsLoading(true)

    const emergencyContact: EmergencyContactModel = {
      name: formData.name,
      mobile: convertMobileForAPI(formData.mobile),
      relationship: formData.relationship,
      email: formData.email,
    }

    try {
      await updateEmergencyContactInfo(user.id, emergencyContact)
      nextStep()
    } catch (err: any) {
      showBanner('error', err)
    }

    setIsLoading(false)
  }

  return (
    <Container>
      <Text variant="header2" align="center">
        Add an emergency contact
      </Text>
      <Box mt={5} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          render={({ field, fieldState }) => (
            <TextInput {...field} label="Name" error={fieldState.error?.message} />
          )}
          name="name"
          control={control}
          defaultValue={existingValues.name}
        />

        <Controller
          render={({ field, fieldState }) => (
            <TextInput {...field} label="Relationship" error={fieldState.error?.message} />
          )}
          name="relationship"
          control={control}
          defaultValue={existingValues.relationship}
        />

        <Controller
          render={({ field, fieldState }) => (
            <TextInput {...field} label="E-mail" error={fieldState.error?.message} />
          )}
          name="email"
          control={control}
          defaultValue={existingValues.email || ''}
        />

        <Controller
          render={({ field, fieldState }) => (
            <PhoneInput {...field} label="Mobile Phone Number" type="tel" error={fieldState.error?.message} />
          )}
          name="mobile"
          control={control}
          defaultValue={existingValues.mobile || ''}
        />

        <Box mt={5} />
        <Button type="submit" size={sizes.extraLarge} isLoading={isLoading} margin="0 auto">
          Continue
        </Button>
      </form>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  width: 100%;
  margin: 0 auto 50px auto;
`
